import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User, Session } from '@supabase/supabase-js';
import type { Database } from '@/types/database';
import { applyTheme } from '@/lib/utils/theme';
import i18n, { SUPPORTED_LANGUAGES } from '@/i18n';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface UseAuthReturn {
  user: (User & { profile?: Profile }) | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  reloadProfile: () => Promise<void>;
}

/**
 * Hook for authentication state and operations
 * 
 * Provides user session, profile data, and auth methods
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<(User & { profile?: Profile }) | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setIsLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        loadProfile(session.user);
      } else {
        setUser(null);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function loadProfile(authUser: User) {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .single();

      // Force new object creation to trigger React re-render
      setUser({ ...authUser, profile: profile || undefined, _reloadId: Date.now() } as any);

      // Apply saved theme and language from DB on every auth session
      if (profile?.theme_preference) {
        applyTheme(profile.theme_preference);
      }
      if (profile?.ui_language && (SUPPORTED_LANGUAGES as readonly string[]).includes(profile.ui_language)) {
        i18n.changeLanguage(profile.ui_language);
        localStorage.setItem('i18nextLng', profile.ui_language);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setUser({ ...authUser, _reloadId: Date.now() } as any);
    } finally {
      setIsLoading(false);
    }
  }

  async function signIn(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  }

  async function signUp(email: string, password: string): Promise<void> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          email, // Ensure email is in metadata
        },
      },
    });

    if (error) throw error;
    
    // Don't throw error if user is null - confirmation email might be pending
    if (!data.user) {
      console.warn('User not immediately available - email confirmation may be pending');
    }
  }

  async function signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  async function resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  }

  async function reloadProfile(): Promise<void> {
    if (session?.user) {
      await loadProfile(session.user);
    }
  }

  return {
    user,
    session,
    isLoading,
    isAuthenticated: !!session,
    signIn,
    signUp,
    signOut,
    resetPassword,
    reloadProfile,
  };
}
